import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import block from 'bem-cn'

import { PaymentMethodsType } from 'services/api/PaymentApi/types/common'
import { ICryptoCurrencyWithMinAndMaxItem, ISystemListItem } from 'services/api/PaymentApi/types/types'

import { MODAL_ROUTE_PATH } from 'router/modalRoutes'

import { ReactComponent as ArrowSvg } from 'shared/img/payment/arrow.svg'

import { paymentNameSeparator } from 'shared/constants/payment'
import { LocalePaymentKeysType, LocalePaymentType } from 'shared/locale/en-US'

import { selectLanguage } from 'features/userSettings/selectors'

import Button, { ButtonSizes } from 'components/UIKit/Button/Button'

import { getIconForPayment, paymentSystemInfo } from '../../data'
import { getCryptoPaymentNotification } from '../CryptoPayment/data/cryptoPaymentNotifications'
import { DepositInfo } from '../DepositInfo/DepositInfo'

import './ChoosePayment.scss'

interface IChoosePayment {
  changeSystem: (system: string) => void
  systemList: ISystemListItem[]
  cryptoList?: ICryptoCurrencyWithMinAndMaxItem[]
  locale: LocalePaymentType
  isLoading: boolean
  selectedSystem?: string
  isCrypto?: boolean
  onGoBack?: () => void
}

const b = block('choose-payment')

export const ChoosePayment: React.FC<IChoosePayment> = ({
  changeSystem,
  systemList,
  cryptoList = [],
  locale,
  isLoading,
  selectedSystem,
  isCrypto = false,
  onGoBack,
}) => {
  const lang = useSelector(selectLanguage, shallowEqual)

  const [focusedSystem, setFocusedSystem] = useState('')

  const location = useLocation()

  // Временно, пока другой крипты нет
  useEffect(() => {
    if (isCrypto) {
      setFocusedSystem('usdttrc20')
    }
  }, [isCrypto])

  const isDepositModal = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const openedModals = params.get('openedModals')
    return openedModals === MODAL_ROUTE_PATH.DEPOSIT
  }, [location.search])

  const localePaymentSystemMethods = useMemo(
    () => locale.selectPaymentSystemMethods as Record<PaymentMethodsType, string>,
    [locale.selectPaymentSystemMethods],
  )

  const onChangePaymentSystem = useCallback(
    (value: string) => {
      changeSystem(selectedSystem ? `${selectedSystem}${paymentNameSeparator}${value}` : value)
    },
    [changeSystem, selectedSystem],
  )

  const cryptoDescription = useMemo(() => {
    if (!isCrypto) return undefined
    return focusedSystem === 'usdttrc20' ? getCryptoPaymentNotification(lang, 'usdttrc20') : undefined
  }, [focusedSystem, isCrypto, lang])

  const paymentSystems = useMemo(
    () =>
      systemList.map(item => ({
        name: localePaymentSystemMethods[item.method] ?? item.method,
        iconName: item.name,
        value: item.slug,
      })),
    [localePaymentSystemMethods, systemList],
  )

  const cryptoSystems = useMemo(
    () =>
      cryptoList.map(item => ({
        name: item.currency.toUpperCase(),
        iconName: item.currency,
        value: item.currency,
      })),
    [cryptoList],
  )

  const allSystems = useMemo(
    () =>
      [...cryptoSystems, ...paymentSystems].map(system =>
        // const icon = getIconForPayment[system.iconName.toLowerCase() as paymentNames] ?? getIconForPayment.default
        ({ ...system /** , icon */ }),
      ),
    [paymentSystems, cryptoSystems],
  )

  const allMethods = useMemo(
    () =>
      allSystems?.map(system => {
        const isFocused = focusedSystem === system.value
        const systemInfo = paymentSystemInfo[system.value]

        return (
          <div
            key={system.name + system.iconName}
            className={b('method', { focused: isFocused })}
            onClick={() => setFocusedSystem(system.value)}>
            {/* <span>{system.name}</span> */}
            {getIconForPayment[system.iconName] && (
              <>
                <img
                  src={getIconForPayment[system.iconName]}
                  alt={system.iconName}
                  className={b('method-icon', { type: system.name.toLowerCase() })}
                />
                <div className={b('method-divider')} />
              </>
            )}

            {/* {isFocused && <CheckmarkSvg className={b('method-checkmark')} />} */}

            <div className={b('item')}>
              {locale[system.value as LocalePaymentKeysType] ?? system.iconName}

              {systemInfo?.commission !== undefined ? (
                <span dir="ltr" className={b('item-commission')}>
                  {systemInfo.commission}% {locale.commission}
                </span>
              ) : null}

              {systemInfo?.standard !== undefined ? (
                <span dir="ltr" className={b('item-standard')}>
                  {systemInfo?.standard}
                </span>
              ) : null}
            </div>
          </div>
        )
      }),
    [allSystems, focusedSystem, locale],
  )

  return (
    <div className={b()}>
      {!isCrypto && <div className={b('title')}>{locale.chooseMethod}</div>}

      {isCrypto && (
        <div className={b('back')}>
          <span className={b('back-arrow')} onClick={onGoBack}>
            <ArrowSvg className={b('back-arrow-icon')} />
          </span>
          <span className={b('back-title')}>{isDepositModal ? locale.deposit : locale.withdraw}</span>
        </div>
      )}

      <div className={b('all-methods')}>
        {!isLoading && allMethods}
        {isLoading && (
          <div className={b('method', { hide: !isLoading })}>
            <div className={b('spinner')} />
          </div>
        )}
      </div>

      {!isCrypto && (
        <div className={b('button-wrapper', { isDepositModal })}>
          <Button
            text={locale.next}
            onClick={() => onChangePaymentSystem(focusedSystem)}
            disabled={!focusedSystem}
            fullWidth
            size={ButtonSizes.LARGE}
          />
        </div>
      )}

      {/* {isDepositModal && <DepositInfo locale={locale} description={cryptoDescription} isCrypto={isCrypto} />} */}

      {isCrypto && (
        <div className={b('button-wrapper', { isCrypto })}>
          <Button
            text={locale.continue}
            onClick={() => onChangePaymentSystem(focusedSystem)}
            disabled={!focusedSystem}
            fullWidth
            size={ButtonSizes.LARGE}
          />
        </div>
      )}
    </div>
  )
}
